/* eslint-disable camelcase */
import { useSelector } from 'react-redux'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { clsx } from 'clsx'

import { notificationPreferencesQuery, notificationPreferencesQueryKey } from './queries'

import styles from './EmailNotificationsSection.module.scss'
import settingsStyles from '@/pages/app/Settings/styles.module.scss'

import { updateNotificationPreferences } from '@/api/enterprise'
import { AdminNotificationPreferencesResponse } from '@/api/generated/api'
import Button from '@/components/Button'
import { Switch } from '@/components/Switch'
import { useToast } from '@/components/Toast/ToastContext'
import { useAnalytics } from '@/hooks/useAnalytics'
import { selectIsAdmin } from '@/store/features/user/userSlice'
import { EventName } from '@/utils/eventDefinitions'

const EmailNotificationsSection = () => {
  const isAdmin = useSelector(selectIsAdmin)
  const queryClient = useQueryClient()
  const { updateToast } = useToast()
  const { trackEvent } = useAnalytics()

  const {
    data: preferences,
    isLoading,
    error,
    refetch,
  } = useQuery({ ...notificationPreferencesQuery(), enabled: isAdmin })

  const updatePreferences = useMutation({
    mutationFn: async (newTeammateDigest: boolean) =>
      (await updateNotificationPreferences({ teammates_added_digest_enabled: newTeammateDigest })).data,

    // Optimistic: flip the cached boolean so the Switch moves on click, not on round-trip.
    onMutate: async (newTeammateDigest) => {
      await queryClient.cancelQueries({ queryKey: notificationPreferencesQueryKey })
      const previous = queryClient.getQueryData<AdminNotificationPreferencesResponse>(notificationPreferencesQueryKey)
      queryClient.setQueryData<AdminNotificationPreferencesResponse>(
        notificationPreferencesQueryKey,
        (old: AdminNotificationPreferencesResponse | undefined) =>
          old
            ? { ...old, teammates_added_digest_enabled: newTeammateDigest }
            : { teammates_added_digest_enabled: newTeammateDigest }
      )
      return { previous }
    },

    onError: (err, newTeammateDigest, context) => {
      if (context?.previous) queryClient.setQueryData(notificationPreferencesQueryKey, context.previous)
      trackEvent?.(EventName.SettingChanged, {
        setting: 'teammatesAddedDigest',
        value: newTeammateDigest,
        success: false,
      })
      updateToast((err as Error)?.message || 'Failed to update email preferences', 'error')
    },

    // onSuccess already writes the authoritative PATCH response into the cache; an onSettled
    // invalidate would re-GET from the read replica and can flicker the switch back on lag.
    onSuccess: (data, newTeammateDigest) => {
      queryClient.setQueryData(notificationPreferencesQueryKey, data)
      trackEvent?.(EventName.SettingChanged, {
        setting: 'teammatesAddedDigest',
        value: newTeammateDigest,
        success: true,
      })
      updateToast('Email preferences updated', 'success')
    },
  })

  const handleToggle = (checked: boolean) => updatePreferences.mutate(checked)

  // This preference only affects the admin-only teammates-added digest, so it's meaningless
  // (and inaccessible — the backend 403s) for a non-admin viewing their own Personal tab.
  // Hooks above still run unconditionally either way (rules of hooks); only the render bails.
  if (!isAdmin) return null

  return isLoading ? (
    <div className={styles.rowStatus}>Loading email preferences…</div>
  ) : error ? (
    <div className={styles.rowStatus}>
      Failed to load email preferences. <Button title="Try again" type="link" onClick={() => void refetch()} />
    </div>
  ) : (
    <div
      className={clsx(settingsStyles.settingsItem, settingsStyles.alignTop)}
      data-testid="new-teammate-digest-setting"
    >
      <div className={settingsStyles.labelBlock}>
        <div className={settingsStyles.toggleLabel}>New teammate notices</div>
        <div className={settingsStyles.labelDescription}>
          Get a daily digest when new team members are added to your team space account.
        </div>
      </div>
      <Switch
        checked={preferences?.teammates_added_digest_enabled ?? true}
        onInputChange={handleToggle}
        disabled={updatePreferences.isPending}
      />
    </div>
  )
}

export default EmailNotificationsSection
